import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useMerchant, useUser } from "@auth";
import { getPortalNotifications } from "@api";
import { cn } from "@utils";
import { channels } from "../../realtime/channels";
import { useCoalescedInvalidation } from "../../realtime/useCoalescedInvalidation";
import { usePaymentRequestDrawer } from "@context/PaymentRequestDrawerContext";
import { useTransactionDrawer } from "@context/TransactionDrawerContext";
import { usePayoutDrawer } from "@context/PayoutDrawerContext";
import { useNoteDrawer } from "@context/NoteDrawerContext";
import { useTaskDrawer } from "@context/TaskDrawerContext";
import { notificationVisual } from "./notificationVisuals";
import { notificationTarget } from "./notificationTarget";
import { useMarkNotificationRead } from "../hooks/usePortalNotifications";

const DURATION = 6000;
const MAX_VISIBLE = 3;
const EXIT_MS = 260;

// Signals the bell to react when a notification pops. Kept as a DOM event so the toaster and the
// bell stay decoupled (either can exist without the other).
export const NOTIFICATION_ARRIVED_EVENT = "portal-notification:arrived";

// Fired by the bell when its popover opens/closes ({ detail: { open } }). The toaster listens so it
// can clear (and stop popping) toasts while the popover is open — the popover already shows the list,
// and both live in the top-right so they'd otherwise overlap.
export const NOTIFICATION_POPOVER_EVENT = "portal-notification:popover";

const KEYFRAMES = `
@keyframes portalToastProgress { from { transform: scaleX(1); } to { transform: scaleX(0); } }
`;

function ToastCard({ toast, onDismiss }) {
  const navigate = useNavigate();
  const { openPaymentRequest } = usePaymentRequestDrawer();
  const { openTransaction } = useTransactionDrawer();
  const { openPayout } = usePayoutDrawer();
  const { openNote } = useNoteDrawer();
  const { openTask } = useTaskDrawer();
  const markRead = useMarkNotificationRead();
  const [entered, setEntered] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [paused, setPaused] = useState(false);

  const remainingRef = useRef(DURATION);
  const startedAtRef = useRef(0);
  const timerRef = useRef(null);
  const exitRef = useRef(null);

  const beginExit = useCallback(() => {
    if (exitRef.current) return;
    setLeaving(true);
    exitRef.current = setTimeout(() => onDismiss(toast.id), EXIT_MS);
  }, [onDismiss, toast.id]);

  const resume = useCallback(() => {
    startedAtRef.current = Date.now();
    timerRef.current = setTimeout(beginExit, remainingRef.current);
  }, [beginExit]);

  const pause = useCallback(() => {
    clearTimeout(timerRef.current);
    remainingRef.current -= Date.now() - startedAtRef.current;
  }, []);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    resume();
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timerRef.current);
      clearTimeout(exitRef.current);
    };
  }, [resume]);

  const onEnter = () => {
    setPaused(true);
    pause();
  };
  const onLeave = () => {
    setPaused(false);
    resume();
  };

  const { Icon, chip } = notificationVisual(toast.type);

  const handleClick = () => {
    // Same as a bell row click: marks the notification read and opens the related entity from
    // (type + primaryEntityId) as a drawer over the current page, falling back to navigation.
    // Dismissing via the X or letting it time out leaves it unread.
    markRead(toast.id);

    const target = notificationTarget(toast);
    if (target?.kind === "paymentRequest") openPaymentRequest(target.id);
    else if (target?.kind === "transaction") openTransaction(target.id, target.paymentMethodKey);
    else if (target?.kind === "payout") openPayout(target.id);
    else if (target?.kind === "note") openNote(target.id);
    else if (target?.kind === "task") openTask(target.id);
    else if (target?.kind === "navigate") navigate(target.url);
    beginExit();
  };

  return (
    <div
      role="status"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      className={cn(
        "pointer-events-auto relative w-[360px] max-w-[calc(100vw-3rem)] overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-zinc-200/70",
        "transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-opacity",
        entered && !leaving
          ? "translate-y-0 scale-100 opacity-100"
          : "-translate-y-3 scale-95 opacity-0 motion-reduce:translate-y-0 motion-reduce:scale-100",
      )}
    >
      <button
        type="button"
        onClick={handleClick}
        className="flex w-full items-start gap-3 p-3 text-left transition-colors hover:bg-zinc-50"
      >
        <span className={cn("flex size-9 shrink-0 items-center justify-center rounded-lg", chip)}>
          <Icon aria-hidden="true" className="size-5" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5 pr-5">
          <span className="truncate text-sm font-semibold text-zinc-900">{toast.title}</span>
          {toast.subtitle && (
            <span className="whitespace-normal break-words text-xs text-zinc-500">{toast.subtitle}</span>
          )}
          <span className="text-[11px] font-medium text-zinc-400">Just now</span>
        </span>
      </button>

      <button
        type="button"
        aria-label="Dismiss"
        onClick={beginExit}
        className="absolute right-2 top-2 flex size-6 items-center justify-center rounded-full text-zinc-300 transition-colors hover:bg-zinc-100 hover:text-zinc-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
      >
        <XMarkIcon aria-hidden="true" className="size-3.5" />
      </button>

      <div className="h-0.5 w-full bg-zinc-100">
        <div
          className="h-full origin-left bg-primary/70 motion-reduce:hidden"
          style={{
            animation: `portalToastProgress ${DURATION}ms linear forwards`,
            animationPlayState: paused ? "paused" : "running",
          }}
        />
      </div>
    </div>
  );
}

// Self-contained realtime notification popups, anchored top-right just below the header so they read
// as dropping from the bell. Owns its own detection: it seeds the set of already-seen notifications
// on first load (so nothing pops on mount) and only toasts genuinely new, unread arrivals thereafter.
export default function NotificationToaster() {
  const { merchant } = useMerchant();
  const { user } = useUser();
  const [toasts, setToasts] = useState([]);
  const seenRef = useRef(null);
  const popoverOpenRef = useRef(false);

  // Opening the bell dismisses any live toasts (you're now looking at the full list) and suppresses
  // new ones while it's open, so a toast never overlaps the popover in the top-right.
  useEffect(() => {
    const onPopover = (e) => {
      const open = !!e.detail?.open;
      popoverOpenRef.current = open;
      if (open) setToasts([]);
    };
    window.addEventListener(NOTIFICATION_POPOVER_EVENT, onPopover);
    return () => window.removeEventListener(NOTIFICATION_POPOVER_EVENT, onPopover);
  }, []);

  const recentQuery = useQuery({
    queryKey: ["notifications", "recent"],
    queryFn: ({ signal }) => getPortalNotifications({ take: 10, signal }),
    staleTime: 30_000,
  });

  // Same coalesced hint→refetch as the bell (one window per tab, shared key prefix, so the two
  // never double-fetch: an in-flight refetch is left to finish rather than restarted).
  const channelPath = merchant?.id && user?.guid ? channels.user(merchant.id, user.guid) : null;
  useCoalescedInvalidation(channelPath, ["notifications", "recent"]);

  useEffect(() => {
    const data = recentQuery.data;
    if (!data) return;

    // First successful load only seeds the baseline — never pops for notifications that were
    // already there before this session.
    if (seenRef.current === null) {
      seenRef.current = new Set(data.map((n) => n.id));
      return;
    }

    const fresh = data.filter((n) => n.status === "Unread" && !seenRef.current.has(n.id));
    if (fresh.length === 0) return;

    fresh.forEach((n) => seenRef.current.add(n.id));

    // The popover already shows the list, so don't also toast while it's open — but still mark these
    // seen so they don't pop later.
    if (popoverOpenRef.current) return;

    // data is newest-first; the stack hangs from the top-right bell, so keep newest at the top.
    setToasts((prev) => [...fresh, ...prev].slice(0, MAX_VISIBLE));
    window.dispatchEvent(new CustomEvent(NOTIFICATION_ARRIVED_EVENT));
  }, [recentQuery.data]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed right-6 top-14 z-50 flex flex-col items-end gap-2"
    >
      <style>{KEYFRAMES}</style>
      {toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} onDismiss={dismiss} />
      ))}
    </div>
  );
}
